import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";

// Site::Lp01::AreaCta::Component の Stimulus コントローラ。
// landIeul: AreaCta の 土地の有無 → 都道府県 → 市区町村 の段階選択を再現。
// 市区町村は GraphQL(GetCities) で取得し optgroup を groupByYomi で構築する。

interface NamedItem {
  id: string;
  name: string;
  yomi: string;
}

const GROUP_MAP: [RegExp, string][] = [
  [/^[ア-オ]/, "ア"],
  [/^[カ-コガ-ゴ]/, "カ"],
  [/^[サ-ソザ-ゾ]/, "サ"],
  [/^[タ-トダ-ド]/, "タ"],
  [/^[ナ-ノ]/, "ナ"],
  [/^[ハ-ホバ-ボパ-ポ]/, "ハ"],
  [/^[マ-モ]/, "マ"],
  [/^[ヤ-ヨ]/, "ヤ"],
  [/^[ラ-ロ]/, "ラ"],
  [/^[ワ-ン]/, "ワ"],
];

function mapToGroup(yomi: string): string {
  const m = GROUP_MAP.find(([re]) => re.test(yomi));
  return m ? m[1] : "その他";
}

function groupByYomi(items: NamedItem[]): { name: string; items: NamedItem[] }[] {
  const map = new Map<string, NamedItem[]>();
  items.forEach((it) => {
    const g = mapToGroup(it.yomi);
    map.set(g, [...(map.get(g) || []), it]);
  });
  return Array.from(map.keys())
    .sort()
    .map((k) => ({
      name: k,
      items: (map.get(k) || []).sort((a, b) =>
        a.yomi < b.yomi ? -1 : a.yomi > b.yomi ? 1 : 0,
      ),
    }));
}

const GET_CITIES = `query GetCities($id: ID!){prefecture(id:$id){cities{id name yomi}}}`;

const CURRENT = "Lp01_current";
const DONE = "Lp01_done";
const BTN_DONE = "Lp01_done";

const HOME = "/plan-request/?area_cta=lp01";
const UTM = "&utm_lp=lp01&utm_cta=";

class SiteLp01AreaCtaController extends Controller {
  static targets = ["possession", "prefecture", "city", "group", "button", "link"];
  static values = { graphql: String, num: String };

  declare readonly possessionTarget: HTMLSelectElement;
  declare readonly prefectureTarget: HTMLSelectElement;
  declare readonly cityTarget: HTMLSelectElement;
  declare readonly groupTargets: HTMLElement[];
  declare readonly buttonTarget: HTMLElement;
  declare readonly linkTarget: HTMLAnchorElement;
  declare readonly graphqlValue: string;
  declare readonly numValue: string;

  private readonly LEVELS = ["possession", "prefecture", "city"];

  async change(event: Event): Promise<void> {
    const level = this.levelOf(event.target as HTMLSelectElement);
    if (level === "possession" && this.possessionTarget.value === "") {
      this.reset();
    } else if (level === "prefecture") {
      await this.reloadCities();
    }
    this.refresh();
  }

  private levelOf(select: HTMLSelectElement): string {
    if (select === this.prefectureTarget) return "prefecture";
    if (select === this.cityTarget) return "city";
    return "possession";
  }

  private async fetchData(query: string, id: string): Promise<any> {
    const res = await fetch(this.graphqlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, variables: { id } }),
    });
    const json = await res.json();
    return json.data;
  }

  private optionsHtml(
    placeholder: string,
    groups: { name: string; items: NamedItem[] }[],
  ): string {
    let html = `<option value="">${placeholder}</option>`;
    groups.forEach((g) => {
      html += `<optgroup label="${g.name}">`;
      g.items.forEach((it) => {
        html += `<option value="${it.id}">${it.name}</option>`;
      });
      html += "</optgroup>";
    });
    return html;
  }

  private async reloadCities(): Promise<void> {
    this.cityTarget.innerHTML = '<option value="">市区町村を選択</option>';
    const pref = this.prefectureTarget.value;
    if (!pref) return;
    const data = await this.fetchData(GET_CITIES, pref);
    const cities: NamedItem[] = data?.prefecture?.cities || [];
    this.cityTarget.innerHTML = this.optionsHtml("市区町村を選択", groupByYomi(cities));
  }

  private reset(): void {
    this.prefectureTarget.value = "";
    this.cityTarget.innerHTML = '<option value="">市区町村を選択</option>';
  }

  // done/current クラスと CTA リンクを再計算 (title / selectForm 両方に付与)
  private refresh(): void {
    const vals = [
      this.possessionTarget.value,
      this.prefectureTarget.value,
      this.cityTarget.value,
    ];
    const activeIdx = vals.findIndex((v) => !v); // 最初の未選択。全選択なら -1

    this.LEVELS.forEach((level, i) => {
      const filled = !!vals[i];
      const isCurrent = i === activeIdx;
      const group = this.groupTargets.find((g) => g.dataset.level === level);
      if (!group) return;
      group.querySelectorAll<HTMLElement>(".Lp01_select__title, .Lp01_selectForm").forEach((el) => {
        el.classList.toggle(DONE, filled);
        el.classList.toggle(CURRENT, isCurrent);
      });
    });

    const complete = activeIdx === -1;
    this.buttonTarget.classList.toggle(BTN_DONE, complete);
    this.linkTarget.href = this.buildHref(vals[1], vals[2]);
  }

  private buildHref(pref: string, city: string): string {
    const num = this.numValue;
    const tail = `pref-id=${pref}&city-id=${city}&utm_campaign=Lp_area`;
    const search = window.location.search;
    return search
      ? `${HOME}${search}${UTM}${num}&${tail}`
      : `${HOME}?${UTM}${num}&${tail}`;
  }
}

application.register("site--lp01--area-cta", SiteLp01AreaCtaController);
